import { useCallback, useEffect, useRef, useState } from "react";

import { getPatientConsultationContext } from "services/ConsultationService";

const TIMER_PADDING_MS = 250;

const MAX_TIMER_DELAY_MS = 2_147_000_000;

const getError = (error) => {
  if (error instanceof Error) {
    return error;
  }

  return new Error("Unable to load consultation context.");
};

const getNextBoundaryDelay = (consultationContext) => {
  const serverNowValue = consultationContext?.serverNow;

  const timing = consultationContext?.timing;

  if (!serverNowValue || !timing) {
    return null;
  }

  const serverNowMs = new Date(serverNowValue).getTime();

  if (!Number.isFinite(serverNowMs)) {
    return null;
  }

  const boundaryValues = [
    timing.waitingOpensAt,
    timing.slotAt,
    timing.hardEndsAt,
  ];

  const futureBoundaries = boundaryValues
    .map((value) => new Date(value).getTime())
    .filter((value) => Number.isFinite(value) && value > serverNowMs)
    .sort((first, second) => first - second);

  if (!futureBoundaries.length) {
    return null;
  }

  const nextBoundaryMs = futureBoundaries[0];

  const delay = nextBoundaryMs - serverNowMs + TIMER_PADDING_MS;

  return Math.min(Math.max(delay, TIMER_PADDING_MS), MAX_TIMER_DELAY_MS);
};

const useConsultationContext = ({ appointmentId, enabled = true } = {}) => {
  const [consultationContext, setConsultationContext] = useState(null);

  const [loading, setLoading] = useState(Boolean(enabled && appointmentId));

  const [error, setError] = useState(null);

  const activeRequestIdRef = useRef(0);

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      if (!enabled || !appointmentId) {
        return null;
      }

      const currentRequestId = activeRequestIdRef.current + 1;

      activeRequestIdRef.current = currentRequestId;

      if (!silent) {
        setLoading(true);
      }

      setError(null);

      try {
        const nextContext = await getPatientConsultationContext(appointmentId);

        if (activeRequestIdRef.current === currentRequestId) {
          setConsultationContext(nextContext);
        }

        return nextContext;
      } catch (requestError) {
        const normalizedError = getError(requestError);

        if (activeRequestIdRef.current === currentRequestId) {
          setError(normalizedError);
        }

        throw normalizedError;
      } finally {
        if (activeRequestIdRef.current === currentRequestId && !silent) {
          setLoading(false);
        }
      }
    },
    [appointmentId, enabled],
  );

  /*
   * Initial load and appointment changes.
   */
  useEffect(() => {
    activeRequestIdRef.current += 1;

    setConsultationContext(null);
    setError(null);

    if (!enabled || !appointmentId) {
      setLoading(false);

      return undefined;
    }

    refresh().catch(() => {
      // Error is already stored by refresh().
    });

    return () => {
      activeRequestIdRef.current += 1;
    };
  }, [appointmentId, enabled, refresh]);

  /*
   * Refresh when the browser tab becomes active again.
   * This prevents a sleeping/background tab from keeping
   * an outdated timing decision.
   */
  useEffect(() => {
    if (!enabled || !appointmentId) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh({
          silent: true,
        }).catch(() => {
          // Error is already stored by refresh().
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [appointmentId, enabled, refresh]);

  /*
   * Refresh at the next server-defined timing boundary.
   * This does not create timing rules on the frontend.
   * It only asks the backend for a fresh decision.
   */
  useEffect(() => {
    if (!enabled || !appointmentId || !consultationContext) {
      return undefined;
    }

    const delay = getNextBoundaryDelay(consultationContext);

    if (delay === null) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      refresh({
        silent: true,
      }).catch(() => {
        // Error is already stored by refresh().
      });
    }, delay);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [appointmentId, consultationContext, enabled, refresh]);

  return {
    consultationContext,
    loading,
    error,
    refresh,
  };
};

export default useConsultationContext;
